import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';
import axios from 'axios';
import camelcaseKeys from 'camelcase-keys';
import confirm from 'components/confirm';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/pro-solid-svg-icons';

import ManagedInput from 'components/form/managed_input';
import './signed_ca.scss';
import LoadingSpinner from 'components/loading_spinner/index';
import CaDropzone from './ca_dropzone';
import Button from 'shared/button';
import { faRotateRight } from '@fortawesome/pro-solid-svg-icons';

const NO_CA_MESSAGE = `This user has not signed a CA. If you have a signed CA from them,
  you can upload it here and they will not have to sign it on the website.`;
const FORCE_SIGNED_BY_USER_MESSAGE = `This CA was signed by a Buildout user on behalf of the lead.
  If you have a signed CA from them, you can upload it here.`;
const REPLACE_CA_MESSAGE = 'Replace the existing CA by clicking or dragging a new one below.';
const CA_UPLOAD_ERROR_MESSAGE = 'There was an error uploading the CA. Please contact support.';

export default function SignedCA({
  caDownloadPath,
  caName,
  caUploadPath,
  isDisabled,
  isValidCa,
  lead,
  onSuccess,
  refreshLead,
  saleOrLease,
  signedCa,
}) {
  const [isCaUploading, setIsCaUploading] = useState(false);
  const [error, setError] = useState(null);
  const [replacingCa, setReplacingCa] = useState(false);
  const disabled = useMemo(() => isDisabled(), [isDisabled]);

  const handleDrop = (file) => {
    setIsCaUploading(true);
    setError(null);
    setReplacingCa(false);
    const formData = new FormData();
    formData.append('signed_ca_file', file);

    axios
      .post(caUploadPath, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(response => onSuccess(camelcaseKeys(response.data)))
      .catch(() => setError(CA_UPLOAD_ERROR_MESSAGE))
      .finally(() => setIsCaUploading(false));
  };

  const handleSignCa = () => {
    // if a lead is already marked as signed on the server, don't allow unsign here
    if (signedCa) return;

    confirm(
      `Are you sure you want to mark your lead "${lead.displayName}" as having signed the confidentiality ` +
      `agreement? This action cannot be undone.`,
      {
        buttonLabel: 'Update Signed CA',
        buttonStyle: 'primary',
        title: `Confirm CA Signed`
      }
    )
      .then(() => {
        axios.post(`/projects/${lead.projectId}/leads/${lead.id}/sign_ca`, { sale_or_lease: saleOrLease })
          .then(() => {
            refreshLead(lead);
          });
      })
      .catch(() => {});
  };


  const renderCaCheckbox = () => {
    if (disabled) return;

    return (
      <div className="pt-3">
        <ManagedInput
          checked={signedCa}
          disabled={signedCa} // Disable checkbox if user has already signed CA
          label="Signed CA"
          type="checkbox"
          onChange={handleSignCa}
        />
      </div>
    );
  };

  function renderCaDownload() {
    if (!caDownloadPath || !caName || replacingCa || isCaUploading) return;

    if (!isValidCa) {
      return (
        <div name="invalid-ca">
          <strong className="text-danger">
            Invalid Confidentiality Agreement
          </strong>
          <div>We are unable to show the signed Confidentiality Agreement</div>
        </div>
      );
    }

    return (
      <div>
        <a
          className={`btn btn-light ${disabled ? 'disabled' : ''}`}
          href={caDownloadPath}
          name="ca-download-link"
          rel="noopener noreferrer"
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          target="_blank"
        >
          <FontAwesomeIcon className="mr-2 text-info" icon={faFilePdf}/>
          <span>{caName}</span>
        </a>
        <Button.Secondary
          className="ml-3"
          disabled={disabled}
          icon={faRotateRight}
          name="replace-ca"
          plain
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          onClick={() => setReplacingCa(true)}
        >
          Replace
        </Button.Secondary>
      </div>
    );
  }

  function renderCaUploader() {
    const replace = caName && caDownloadPath;
    const displayUpload = replace ? replacingCa : true;
    if (isCaUploading || !displayUpload) return;

    const getMessageText = () => {
      if (replace) return REPLACE_CA_MESSAGE;
      if (signedCa) return FORCE_SIGNED_BY_USER_MESSAGE;
      return NO_CA_MESSAGE;
    };

    return (
      <div className="mb-4">
        <p name={'no-ca-message'}>{getMessageText()}</p>
        {error && <Alert bsStyle="danger">{error}</Alert>}

        <CaDropzone isDisabled={isDisabled} onFileSelected={handleDrop}/>
      </div>
    );
  }

  return (
    <div>
      {isCaUploading && <LoadingSpinner/>}
      {renderCaDownload()}
      {renderCaUploader()}
      {renderCaCheckbox()}
    </div>
  );
}

SignedCA.propTypes = {
  caDownloadPath: PropTypes.string,
  caName: PropTypes.string,
  caUploadPath: PropTypes.string,
  isDisabled: PropTypes.func,
  isValidCa: PropTypes.bool,
  lead: PropTypes.shape({
    signedCa: PropTypes.bool.isRequired
  }).isRequired,
  onSuccess: PropTypes.func,
  refreshLead: PropTypes.func.isRequired,
  saleOrLease: PropTypes.string.isRequired,
  signedCa: PropTypes.bool.isRequired
};
